import { useSEO } from "../hooks/useSEO";

const PolicyShell = ({ kicker, title, children, testid }) => (
  <div data-testid={testid} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="sb-mono-label">{kicker}</div>
    <h1 className="mt-2 text-4xl sm:text-5xl font-semibold text-[#1B3A5C] tracking-tight" style={{ fontFamily: "Outfit" }}>
      {title}
    </h1>
    <div className="mt-10 space-y-6 text-[#475569] leading-relaxed text-[15px]">{children}</div>
  </div>
);

const H = ({ children }) => (
  <h2 className="text-xl font-medium text-[#1B3A5C] mt-10 mb-2" style={{ fontFamily: "Outfit" }}>
    {children}
  </h2>
);

const Privacy = () => {
  useSEO({
    title: "Privacy Policy",
    description:
      "Sterling Bio Ltd privacy policy. How we collect, store and use the personal data of UK research customers in compliance with UK GDPR and the Data Protection Act 2018.",
    path: "/privacy-policy",
  });

  return (
    <PolicyShell kicker="Legal" title="Privacy Policy" testid="privacy-page">
      <p className="text-sm text-[#64748B] font-mono uppercase tracking-wider">Last updated: 24 April 2026</p>
      <p>
        Sterling Bio Ltd (&ldquo;Sterling Bio&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) is committed to protecting
        your personal data. This Privacy Policy explains what data we collect when you use
        sterlingbio.co.uk, how we use it, and the rights you have under the UK General Data
        Protection Regulation (UK GDPR) and the Data Protection Act 2018.
      </p>

      <H>1. Who we are</H>
      <p>
        Sterling Bio Ltd is the data controller for personal data processed through this
        website. We are a company registered in the United Kingdom. You can reach our team at{" "}
        <a href="mailto:Info@sterlingbio.co.uk" className="text-[#2C8C9C] sb-link-underline">
          Info@sterlingbio.co.uk
        </a>
        .
      </p>

      <H>2. What data we collect</H>
      <p>We only collect the personal data we need to fulfil your order. Specifically:</p>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li><strong>Identity &amp; contact</strong>: full name, email address, phone number.</li>
        <li><strong>Delivery</strong>: shipping address (line 1, line 2, city, postcode, country).</li>
        <li><strong>Order</strong>: items ordered, quantities, totals, payment reference and any notes you choose to add.</li>
        <li><strong>Technical</strong>: limited browser/device information automatically logged by our hosting provider for security and abuse-prevention purposes.</li>
      </ul>
      <p>
        We do <strong>not</strong> collect payment-card data. Payment is settled by direct UK
        bank transfer to our Tide business account; bank-card numbers never touch this website.
      </p>

      <H>3. Why we collect it (lawful basis)</H>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>
          <strong>Contract performance</strong> &mdash; to process your order, take payment, dispatch goods and provide customer service.
        </li>
        <li>
          <strong>Legal obligation</strong> &mdash; to keep accounting and tax records required by UK law (typically 6 years).
        </li>
        <li>
          <strong>Legitimate interest</strong> &mdash; to keep the website secure, detect fraud and improve our service.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> use your data for marketing without your explicit consent and we do not sell or rent it to anyone.
      </p>

      <H>4. Who we share it with</H>
      <p>Your personal data is shared only with a small number of trusted service providers acting on our behalf:</p>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li><strong>Microsoft 365</strong> (email delivery for order confirmations and shipping notifications).</li>
        <li><strong>Our cloud hosting provider</strong> (application, database and file hosting).</li>
        <li><strong>UK courier partners</strong> (Royal Mail and equivalent — for shipping only, name and address only).</li>
        <li><strong>Tide</strong> (our business bank) for payment verification.</li>
        <li><strong>HMRC and other UK authorities</strong> where required by law.</li>
      </ul>

      <H>5. International transfers</H>
      <p>
        Where any of our processors store data outside the UK/EEA (for example, certain
        Microsoft 365 data centres), the transfer is covered by the UK&apos;s Adequacy
        Regulations and/or the standard contractual clauses, ensuring an equivalent level of
        protection.
      </p>

      <H>6. How long we keep it</H>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li><strong>Active orders</strong>: while the order is open and processed.</li>
        <li><strong>Completed orders</strong>: kept in our records for up to 6 years to satisfy UK accounting and tax legislation.</li>
        <li><strong>Archived/deleted orders</strong>: when our admin deletes an archived order from the system, the order record is permanently removed.</li>
      </ul>

      <H>7. Cookies &amp; local storage</H>
      <p>
        We use minimal browser storage to deliver core site functionality:
      </p>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li><strong>Cart contents</strong> &mdash; stored in your browser&apos;s local storage so your basket survives a page refresh. Cleared when you place an order or empty the cart.</li>
        <li><strong>Admin session token</strong> &mdash; only set if you log in to the internal admin dashboard. Cleared on sign-out and after 12 hours.</li>
      </ul>
      <p>
        We do not use third-party advertising or tracking cookies. We do not run analytics that
        share data with advertisers.
      </p>

      <H>8. Your rights</H>
      <p>Under UK GDPR you have the right to:</p>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>Access the personal data we hold about you.</li>
        <li>Have inaccurate data corrected.</li>
        <li>Have your data erased (subject to our legal record-keeping obligations).</li>
        <li>Restrict or object to certain processing.</li>
        <li>Request your data in a portable format.</li>
        <li>Withdraw consent at any time where processing is based on consent.</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href="mailto:Info@sterlingbio.co.uk" className="text-[#2C8C9C] sb-link-underline">
          Info@sterlingbio.co.uk
        </a>
        . We will respond within one month.
      </p>

      <H>9. Security</H>
      <p>
        Our website uses SSL/TLS 256-bit encryption on every page. Admin access is protected
        by bcrypt-hashed passwords and short-lived session tokens. We review our security
        controls regularly. No system is ever 100% secure, so we encourage you to use a strong,
        unique password for any account you create with us.
      </p>

      <H>10. Complaints</H>
      <p>
        If you are unhappy with how we have handled your personal data, please contact us first
        so we can put it right. You also have the right to lodge a complaint with the UK
        Information Commissioner&apos;s Office (ICO) at{" "}
        <a
          href="https://ico.org.uk"
          target="_blank"
          rel="noreferrer"
          className="text-[#2C8C9C] sb-link-underline"
        >
          ico.org.uk
        </a>{" "}
        or by calling 0303 123 1113.
      </p>

      <H>11. Changes to this policy</H>
      <p>
        We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo;
        date above will always reflect the current version.
      </p>
    </PolicyShell>
  );
};

export default Privacy;
